import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * A small history router: the app has a handful of screens and no nested layouts yet. After every
 * navigation, focus moves to the main landmark so screen-reader users hear the new page (WCAG 2.4.3).
 */
type Router = {
  path: string;
  search: URLSearchParams;
  navigate(to: string, opts?: { replace?: boolean }): void;
};

const RouterContext = createContext<Router | null>(null);

const current = () => ({ path: globalThis.location.pathname, search: globalThis.location.search });

export function RouterProvider({ children }: { children: ReactNode }) {
  const [loc, setLoc] = useState(current);

  useEffect(() => {
    const onPop = () => setLoc(current());
    globalThis.addEventListener('popstate', onPop);
    return () => globalThis.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: string, opts: { replace?: boolean } = {}) => {
    const url = new URL(to, globalThis.location.origin);
    if (url.origin !== globalThis.location.origin) throw new Error('navigate() is for app paths only');
    const next = `${url.pathname}${url.search}${url.hash}`;
    if (opts.replace) globalThis.history.replaceState(null, '', next);
    else globalThis.history.pushState(null, '', next);
    setLoc(current());
    globalThis.scrollTo?.(0, 0);
    queueMicrotask(() => document.getElementById('main')?.focus({ preventScroll: true }));
  }, []);

  const value = useMemo<Router>(() => ({ path: loc.path, search: new URLSearchParams(loc.search), navigate }), [loc, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): Router {
  const r = useContext(RouterContext);
  if (!r) throw new Error('useRouter outside RouterProvider');
  return r;
}
